import { Formik } from "formik";
import { debounce } from "lodash";
import { useCallback } from "react";
import { Col, Form, Row } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { MenuItem, MenuItems } from "../../../../../components/@types";
import { DropdownFilter } from "../../../../../components/DropdownFilter";
import SelectWithPaging from "../../../../../components/form/select/SelectWithPaging";
import ClassificationSelect from "../components/ClassificationSelect";
import QuickSearchButtonsGroup from "../components/QuickSearchButtonsGroup";
import TimeRangeSelect from "../components/TimeRangeSelect";
import {
  CUSTOM_SELECT_CLASSNAMES,
  SEARCH_DEBOUNCE_TIME,
  TEST_ID_KEYS
} from "../consts";
import {
  getAttackTechniqueMenuItems,
  getAvVendorsMenuItems,
  getFileNameOptionsMenuItems,
  getFileTagsMenuItems,
  getFileTypeMenuItems,
  getTcIndicatorMenuItems
} from "../menuItems";
import { IFileSearchFormProps, QSFileFormValues } from "./@types";
import { CustomDropdownIndicator } from "./components/CustomDropdownIndicator";
import { CustomInput } from "./components/CustomInput";
import { CustomMultiValueContainer } from "./components/CustomMultiValueContainer";
import { CustomNoOptionsMessage } from "./components/CustomNoOptionMessage";
import { CustomOption } from "./components/CustomOption";
import FileSizeField from "./components/FileSizeField";
import { INITIAL_FILE_FORM_VALUES } from "./initialFormValues";
import {
  convertFormValuesToQuery,
  getFuzzySearchResults,
  preventTypingNonNumericCharacters
} from "./utils";
import { FILE_VALIDATION_SCHEMA } from "./validationSchema";

const FileSearchView = ({ onSubmit, onClose }: IFileSearchFormProps) => {
  const onFormSubmit = (values: QSFileFormValues) => {
    onSubmit(convertFormValuesToQuery(values));
  };

  const handleGetStaticAnalysisIndicatorsMenuItems = useCallback(
    (inputValue: string) =>
      getFuzzySearchResults(inputValue, getTcIndicatorMenuItems(), [
        "listValue",
        "label"
      ]),
    []
  );

  return (
    <Formik
      onSubmit={onFormSubmit}
      initialValues={INITIAL_FILE_FORM_VALUES}
      validationSchema={FILE_VALIDATION_SCHEMA}
    >
      {({
        handleSubmit,
        values,
        handleChange,
        errors,
        setFieldValue,
        handleReset,
        dirty
      }) => (
        <Form
          noValidate
          onSubmit={handleSubmit}
          className={"quick-search__form-container"}
        >
          <Row>
            <Col>
              <Form.Label className={"form-text--important"}>
                File Name:
              </Form.Label>
              <Row>
                <Col className={"col-reduced-padding"} xs={4}>
                  <DropdownFilter
                    title={"Contains"}
                    field={TEST_ID_KEYS.FILE_NAME_OPTION}
                    menuItems={getFileNameOptionsMenuItems()}
                    activeFilter={values.fileNameOption}
                    handleFilterChange={value => {
                      setFieldValue("fileNameOption", value);
                    }}
                    containerClassName={"quick-search__dropdown-container"}
                  />
                </Col>
                <Col className={"col-reduced-padding"} xs={8}>
                  <Form.Control
                    type="text"
                    placeholder="Enter File Name"
                    name={"fileName"}
                    value={values.fileName}
                    onChange={handleChange}
                    isInvalid={!!errors.fileName}
                    data-testid={`${TEST_ID_KEYS.FILE_NAME}_field_input`}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label className={"form-text--important"}>
                      File Type:
                    </Form.Label>
                    <div data-testid={`${TEST_ID_KEYS.FILE_TYPE}_select`}>
                      <AsyncSelect<MenuItem, true>
                        isMulti
                        isSearchable
                        cacheOptions
                        testIdKey={TEST_ID_KEYS.FILE_TYPE}
                        classNames={CUSTOM_SELECT_CLASSNAMES}
                        defaultOptions
                        value={values.fileType}
                        loadOptions={debounce(
                          (
                            inputValue: string,
                            callback: (options: MenuItems) => void
                          ) =>
                            callback(
                              getFuzzySearchResults(
                                inputValue,
                                getFileTypeMenuItems(),
                                ["label"]
                              )
                            ),
                          SEARCH_DEBOUNCE_TIME
                        )}
                        onChange={value => setFieldValue("fileType", value)}
                        closeMenuOnSelect
                        placeholder="Select from List"
                        maxMenuHeight={200}
                        menuShouldScrollIntoView={false}
                        openMenuOnClick
                        components={{
                          Input: CustomInput,
                          Option: CustomOption,
                          DropdownIndicator: CustomDropdownIndicator,
                          MultiValueContainer: CustomMultiValueContainer,
                          NoOptionsMessage: CustomNoOptionsMessage
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label className={"form-text--important"}>
                      File Tags:
                    </Form.Label>
                    <div data-testid={`${TEST_ID_KEYS.FILE_TAGS}_select`}>
                      <AsyncSelect<MenuItem, true>
                        isMulti
                        isSearchable
                        cacheOptions
                        testIdKey={TEST_ID_KEYS.FILE_TAGS}
                        classNames={CUSTOM_SELECT_CLASSNAMES}
                        value={values.fileTags}
                        defaultOptions
                        loadOptions={debounce(
                          (
                            inputValue: string,
                            callback: (options: MenuItems) => void
                          ) =>
                            callback(
                              getFuzzySearchResults(
                                inputValue,
                                getFileTagsMenuItems(),
                                ["label"]
                              )
                            ),
                          SEARCH_DEBOUNCE_TIME
                        )}
                        onChange={(value: MenuItems) =>
                          setFieldValue("fileTags", value)
                        }
                        closeMenuOnSelect
                        placeholder="Select from List"
                        maxMenuHeight={200}
                        menuShouldScrollIntoView={false}
                        openMenuOnClick
                        components={{
                          Input: CustomInput,
                          Option: CustomOption,
                          DropdownIndicator: CustomDropdownIndicator,
                          MultiValueContainer: CustomMultiValueContainer,
                          NoOptionsMessage: CustomNoOptionsMessage
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  File Size:
                </Form.Label>
                <Row>
                  <Col>
                    <FileSizeField
                      placeholder={"From"}
                      fieldName={"fromSizeNumber"}
                      fileSizeOptionsName={"fromSizeType"}
                      onChangeInput={handleChange}
                      onChangeSelect={value =>
                        setFieldValue("fromSizeType", value)
                      }
                      inputFieldErrorMessage={errors.fromSizeNumber}
                      inputValue={values.fromSizeNumber}
                      selectValue={values.fromSizeType}
                      testIdKey={TEST_ID_KEYS.FROM_SIZE_NUMBER}
                    />
                  </Col>
                  <Col>
                    <FileSizeField
                      placeholder={"To"}
                      fieldName={"toSizeNumber"}
                      fileSizeOptionsName={"toSizeType"}
                      onChangeInput={handleChange}
                      onChangeSelect={value =>
                        setFieldValue("toSizeType", value)
                      }
                      inputFieldErrorMessage={errors.toSizeNumber}
                      inputValue={values.toSizeNumber}
                      selectValue={values.toSizeType}
                      testIdKey={TEST_ID_KEYS.TO_SIZE_NUMBER}
                    />
                  </Col>
                </Row>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Row>
                <Col>
                  <ClassificationSelect
                    isMulti
                    value={values.classification}
                    onChange={value => setFieldValue("classification", value)}
                  />
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label className={"form-text--important"}>
                      Threat Score:
                    </Form.Label>
                    <Form.Control
                      type={"number"}
                      placeholder="Enter Value from 1 to 10"
                      name={"threatScore"}
                      onChange={handleChange}
                      value={values.threatScore}
                      data-testid={`${TEST_ID_KEYS.THREAT_SCORE}_field_input`}
                      isInvalid={!!errors.threatScore}
                      onKeyDown={preventTypingNonNumericCharacters}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  Threat Name:
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Threat Name"
                  name={"threatName"}
                  onChange={handleChange}
                  value={values.threatName}
                  data-testid={`${TEST_ID_KEYS.THREAT_NAME}_field_input`}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  Attack Technique:
                </Form.Label>
                <div data-testid={`${TEST_ID_KEYS.ATTACK_TECHNIQUE}_select`}>
                  <AsyncSelect<MenuItem, true>
                    isSearchable
                    isMulti
                    cacheOptions
                    testIdKey={TEST_ID_KEYS.ATTACK_TECHNIQUE}
                    classNames={CUSTOM_SELECT_CLASSNAMES}
                    defaultOptions
                    value={values.attackTechnique}
                    loadOptions={(
                      inputValue,
                      callback: (options: MenuItems) => void
                    ) => {
                      callback(
                        getFuzzySearchResults(
                          inputValue,
                          getAttackTechniqueMenuItems(),
                          ["label"]
                        )
                      );
                    }}
                    onChange={value => setFieldValue("attackTechnique", value)}
                    closeMenuOnSelect
                    placeholder="Select from List"
                    maxMenuHeight={200}
                    menuShouldScrollIntoView={false}
                    openMenuOnClick
                    components={{
                      Input: CustomInput,
                      Option: CustomOption,
                      DropdownIndicator: CustomDropdownIndicator,
                      MultiValueContainer: CustomMultiValueContainer,
                      NoOptionsMessage: CustomNoOptionsMessage
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Label className={"form-text--important"}>
                Antivirus Detections Count:
              </Form.Label>
              <Row>
                <Col>
                  <Form.Control
                    type="number"
                    placeholder="From"
                    name={"antivirusDetectionsCountFrom"}
                    value={values.antivirusDetectionsCountFrom}
                    onChange={handleChange}
                    isInvalid={!!errors.antivirusDetectionsCountFrom}
                    data-testid={`${TEST_ID_KEYS.ANTIVIRUS_DETECTIONS_COUNT_FROM}_field_input`}
                    onKeyDown={preventTypingNonNumericCharacters}
                  />
                </Col>
                <Col>
                  <Form.Control
                    type="number"
                    placeholder="To"
                    name={"antivirusDetectionsCountTo"}
                    value={values.antivirusDetectionsCountTo}
                    onChange={handleChange}
                    isInvalid={!!errors.antivirusDetectionsCountTo}
                    data-testid={`${TEST_ID_KEYS.ANTIVIRUS_DETECTIONS_COUNT_TO}_field_input`}
                    onKeyDown={preventTypingNonNumericCharacters}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  Matched AV Vendors:
                </Form.Label>
                <div data-testid={`${TEST_ID_KEYS.AV_VENDORS}_select`}>
                  <AsyncSelect<MenuItem, true>
                    isMulti
                    isSearchable
                    cacheOptions
                    defaultOptions
                    testIdKey={TEST_ID_KEYS.AV_VENDORS}
                    classNames={CUSTOM_SELECT_CLASSNAMES}
                    value={values.avVendors}
                    loadOptions={debounce(
                      (
                        inputValue: string,
                        callback: (options: MenuItems) => void
                      ) => {
                        callback(
                          getFuzzySearchResults(
                            inputValue,
                            getAvVendorsMenuItems(),
                            ["label"]
                          )
                        );
                      },
                      SEARCH_DEBOUNCE_TIME
                    )}
                    onChange={(value: MenuItems) => {
                      setFieldValue("avVendors", value);
                    }}
                    closeMenuOnSelect
                    placeholder="Select from List"
                    maxMenuHeight={180}
                    menuShouldScrollIntoView={false}
                    openMenuOnClick
                    components={{
                      Input: CustomInput,
                      Option: CustomOption,
                      DropdownIndicator: CustomDropdownIndicator,
                      MultiValueContainer: CustomMultiValueContainer,
                      NoOptionsMessage: CustomNoOptionsMessage
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  Static Analysis Indicators:
                </Form.Label>
                <div
                  data-testid={`${TEST_ID_KEYS.STATIC_ANALYSIS_INDICATORS}_select`}
                >
                  <SelectWithPaging<MenuItem, true>
                    isMulti
                    testIdKey={TEST_ID_KEYS.STATIC_ANALYSIS_INDICATORS}
                    classNames={CUSTOM_SELECT_CLASSNAMES}
                    value={values.staticAnalysisIndicators}
                    menuOptions={handleGetStaticAnalysisIndicatorsMenuItems}
                    onChange={(value: MenuItems) =>
                      setFieldValue("staticAnalysisIndicators", value)
                    }
                    closeMenuOnSelect
                    placeholder="Select from List"
                    maxMenuHeight={180}
                    menuShouldScrollIntoView={false}
                    openMenuOnClick
                    components={{
                      Input: CustomInput,
                      Option: CustomOption,
                      DropdownIndicator: CustomDropdownIndicator,
                      MultiValueContainer: CustomMultiValueContainer,
                      NoOptionsMessage: CustomNoOptionsMessage
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={2}>
              <TimeRangeSelect
                value={values.submissionTime}
                onChange={value => setFieldValue("submissionTime", value)}
                title={"First Seen"}
                errors={errors.submissionTime}
              />
            </Col>
          </Row>
          <div className={"horizontal-divider"} />
          <QuickSearchButtonsGroup
            onSubmit={handleSubmit}
            onCancel={onClose}
            onClear={handleReset}
            searchDisabled={Object.keys(errors).length > 0 || !dirty}
          />
        </Form>
      )}
    </Formik>
  );
};

export default FileSearchView;
